// Persisted reader highlights + notes + bookmarks.
//
// Anchor strategy: every entry stores BOTH a CFI range (the precise text
// location inside the EPUB) AND the literal text content. CFI lets us
// re-paint the highlight after the iframe re-renders; the literal text is
// used for markdown export and as a fallback when a CFI fails to resolve
// (e.g. on a later edition of the same book).

export type HighlightColor =
  | "peach"
  | "yellow"
  | "pink"
  | "green"
  | "blue"
  | "orange";

/** Where an annotation is allowed to travel. Everything starts "private";
 *  nothing becomes shared/public unless the user changes it. This is the
 *  publish-readiness field the Nostr layer will key on later. */
export type Visibility = "private" | "shared" | "public";

/** What kind of annotation this row is. A "highlight" anchors to a passage;
 *  a "review" is book-level (rating + prose, no CFI). Both live in one table
 *  and flow through one pipeline (VISION.md §5, §7). */
export type AnnotationKind = "highlight" | "review";

export interface Highlight {
  /** uuid */
  id: string;
  bookId: string;
  /** epub.js CFI range, e.g. "epubcfi(/6/4!/4/2/4,/1:23,/1:65)". Empty for
   *  book-level annotations (reviews). */
  cfiRange: string;
  /** Literal selected text — used for export + display. Empty for reviews. */
  text: string;
  /** Surrounding text snippet (preceding + following) for context display
   *  in the notes panel. Best-effort. */
  context?: string;
  /** Chapter / spine href the selection belongs to. */
  href?: string;
  /** Chapter label at the time of capture, used for markdown export. */
  chapterLabel?: string;
  color: HighlightColor;
  /** Optional user note attached to this highlight (or the review body). */
  note?: string;
  /** ms epoch */
  createdAt: number;
  updatedAt: number;

  // ── Portable annotation model (2026-07-17, VISION.md §12). All optional
  //    and additive so existing highlights keep working; new writes fill them
  //    in. These are what make an annotation survive independently of the app
  //    and become a signed event later.
  /** Free-form tags: the built-in set (see DEFAULT_TAGS) plus user tags. */
  tags?: string[];
  /** Publish state. Defaults to "private" everywhere it's read. */
  visibility?: Visibility;
  /** "highlight" (default) or "review". */
  kind?: AnnotationKind;
  /** 1..5 stars — used by reviews, optional on highlights. */
  rating?: number;
  /** Content fingerprint of `text` — an edition-tolerant anchor that lets an
   *  annotation relocate across compatible copies where a CFI won't resolve. */
  textHash?: string;
  /** An open question the reader wants answered later. */
  followUp?: string;
  /** AI companion output, kept explicitly separate from the user's own words. */
  aiResponse?: string;
  /** BCP-47 language of the note/review, for the shared layer. */
  language?: string;
}

export interface Bookmark {
  id: string;
  bookId: string;
  /** Position CFI at the moment of bookmark — usually the start of the page. */
  cfi: string;
  href?: string;
  chapterLabel?: string;
  /** Optional one-line label the user typed in. */
  label?: string;
  createdAt: number;
}

export const HIGHLIGHT_COLORS: Array<{ id: HighlightColor; label: string; hex: string }> = [
  { id: "peach", label: "Peach", hex: "#f9d9a8" },
  { id: "yellow", label: "Yellow", hex: "#ffec6b" },
  { id: "pink", label: "Pink", hex: "#ffb3c8" },
  { id: "green", label: "Green", hex: "#a8e6a3" },
  { id: "blue", label: "Blue", hex: "#a9d8ff" },
  { id: "orange", label: "Orange", hex: "#ffc185" },
];

/** Built-in starter tags (VISION.md §3). Users can add their own on top. */
export const DEFAULT_TAGS: string[] = [
  "Important",
  "Funny",
  "Beautiful",
  "Disagree",
  "Research",
  "Follow up",
  "Character",
  "Theme",
  "Historical context",
  "Personal memory",
  "Possible quotation",
  "Unclear",
  "Prediction",
];
